/**
 * Overview Tab Component (Refactored)
 * Main dashboard overview - composes section components
 * Now with pattern recognition and AI-powered insights
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { INDUSTRY_CONFIG } from '@/config/dashboardConfig';
import { InsightSummaryCard } from '../../insights/InsightEngineComponents';
import { PatternAwareInsightCard, SeasonalOutlookBanner } from '../../insights/PatternAwareInsights';
import { CreditsWidget } from '@/components/widgets/CreditsWidget';
import EconomicIndicatorsWidget from '../EconomicIndicatorsWidget';
import CorvanaIntelligenceWidget from '../CorvanaIntelligenceWidget';
import { SPLHComparisonDashboard } from '../SPLHComparisonDashboard';
import { LocationComparisonMode } from '../LocationComparisonMode';
import { UpsellTeasers } from '../UpsellTeasers';
import AISavingsFinderTile from '../AISavingsFinderTile';
import { useAuth } from '@/contexts/AuthContext';
import { useClient } from '@/contexts/ClientContext';
import { TrendingUp, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getQSRDashboardMetrics, getQSRKpis, getQSRLiveMetrics, getQSRWins } from '@/config/qsrLocationMetrics';
import {
  WinsSection,
  ConcernsSection,
  OpportunitiesSection,
  FinancialSection,
  PerformanceSection,
  MarketSection,
  PlanningSection,
  KPISection,
  LiveMetricsBar,
  TasksRecommendationsRow,
  QuickStatsGrid,
  QuickLinksSection,
  RecentActivitySection,
} from './OverviewSections';

export const OverviewTab = ({
  // User/Business context
  userName,
  businessId,
  businessName,
  industry,
  lastLogin,
  canSeeTeamView,
  // Unified API data
  unifiedMetrics,
  unifiedWins,
  unifiedOpportunities,
  summaryData,
  // Local state data
  kpis,
  tasks,
  recommendations,
  liveMetrics,
  // Counts
  completedCount,
  pendingCount,
  highPriorityCount,
  // Widget visibility
  isWidgetEnabled,
  // Handlers
  onToggleTask,
  onExpandTask,
  onExpandCard,
  setActiveTab,
  setFilter,
  isDemoAccount = false,
  dataSources = [],
}) => {
  const navigate = useNavigate();
  const { user} = useAuth();
  const { currentLocation } = useClient();
  const industryConfig = INDUSTRY_CONFIG[industry] || INDUSTRY_CONFIG.hospitality;
  const enabledModules = user?.modules || [];
  const isQSR = industry === 'qsr';

  // QSR location-filtered metrics
  const qsrMetrics = isQSR ? getQSRDashboardMetrics(currentLocation) : null;
  const effectiveMetrics = isQSR ? qsrMetrics : unifiedMetrics;
  const effectiveKpis = isQSR ? getQSRKpis(currentLocation) : kpis;
  const effectiveLiveMetrics = isQSR ? getQSRLiveMetrics(currentLocation) : liveMetrics;
  const effectiveWins = isQSR ? getQSRWins(currentLocation) : unifiedWins;
  const effectiveBusinessName = isQSR && currentLocation
    ? `The Daily Grill — ${currentLocation.shortName}`
    : businessName;

  // Build metrics object for pattern analysis
  const patternMetrics = {
    revenue: effectiveMetrics?.revenue?.current || 125000,
    revenue_previous: effectiveMetrics?.revenue?.previous || 115000,
    customers: effectiveMetrics?.customers?.current || 3200,
    customers_previous: effectiveMetrics?.customers?.previous || 2900,
    avg_transaction: effectiveMetrics?.avg_transaction || 85,
    revenue_change: effectiveMetrics?.revenue_change_pct || 8.7,
    revenue_change_pct: effectiveMetrics?.revenue_change_pct || 8.7,
    customer_change: effectiveMetrics?.customer_change_pct || 10.3,
    customer_change_pct: effectiveMetrics?.customer_change_pct || 10.3,
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* === Full-Width Hero Sections === */}
      <PatternAwareInsightCard
        userName={userName}
        businessId={businessId || 'demo'}
        businessName={effectiveBusinessName}
        industry={industry}
        metrics={patternMetrics}
        lastLogin={lastLogin}
        sources={effectiveMetrics?.data_sources || ['xero', 'square', 'google_analytics']}
      />
      
      <SeasonalOutlookBanner industry={industry} />
      
      {isQSR && <SPLHComparisonDashboard />}
      {isQSR && <LocationComparisonMode />}
      
      {/* Sections stack full-width, top-to-bottom, so rich cards never get
          squeezed into narrow columns where content truncates. */}
      <div className="flex flex-col gap-6 sm:gap-8">
        <WinsSection 
          isEnabled={isWidgetEnabled('recent-wins')} 
          unifiedWins={effectiveWins} 
          industry={industry} 
        />
        
        <ConcernsSection 
          isEnabled={isWidgetEnabled('business-concerns')} 
          canSeeTeamView={canSeeTeamView}
          industry={industry}
        />
        
        <OpportunitiesSection 
          isEnabled={isWidgetEnabled('opportunities')} 
          unifiedOpportunities={unifiedOpportunities}
          industry={industry}
        />

        {isWidgetEnabled('ai-savings-finder') && <AISavingsFinderTile />}

        <PerformanceSection
          canSeeTeamView={canSeeTeamView}
          isStaffEnabled={isWidgetEnabled('staff-performance')}
          isSentimentEnabled={isWidgetEnabled('customer-sentiment')}
          industry={industry}
          onExpandCard={onExpandCard}
          isDemo={isDemoAccount}
        />

        <MarketSection
          canSeeTeamView={canSeeTeamView}
          isCompetitorEnabled={isWidgetEnabled('competitor-intelligence')}
          isSupplierEnabled={isWidgetEnabled('supplier-performance')}
          industry={industry}
          onExpandCard={onExpandCard}
        />

        <PlanningSection
          canSeeTeamView={canSeeTeamView}
          isWeatherEnabled={isWidgetEnabled('weather-impact')}
          isGoalEnabled={isWidgetEnabled('goal-tracker')}
          industry={industry}
          onExpandCard={onExpandCard}
        />
      
        <QuickStatsGrid
          completedCount={completedCount}
          pendingCount={pendingCount}
          highPriorityCount={highPriorityCount}
          kpis={kpis}
          setActiveTab={setActiveTab}
          setFilter={setFilter}
        />
        
        <RecentActivitySection industry={industry} currentLocation={currentLocation} />
      </div>
      
      {/* === Full-Width Bottom Sections === */}
      {/* LiveBenchmarkPanel moved to Market Intel (gated module) */}
      {user && (
        <Card className="border border-dashed border-border/60 bg-muted/20">
          <CardContent className="flex items-center justify-between py-5 px-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Live Industry Benchmarks</p>
                <p className="text-xs text-muted-foreground">Compare your metrics against 25+ Australian industry sources</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="text-xs"
              onClick={() => navigate('/app/market-intelligence')}
              data-testid="benchmarks-market-intel-link"
            >
              Open Market Intel
              <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </CardContent>
        </Card>
      )}
      
      {user && (
        <EconomicIndicatorsWidget 
          industry={industry}
        />
      )}

      <FinancialSection
        canSeeTeamView={canSeeTeamView}
        isRevenueForecastEnabled={isWidgetEnabled('revenue-forecast')}
        isPLEnabled={isWidgetEnabled('pl-snapshot')}
        unifiedMetrics={effectiveMetrics}
        industry={industry}
        dataSources={dataSources}
      />
      
      <KPISection kpis={effectiveKpis} industry={industry} dataSources={isQSR ? ['summit_qsr', 'square'] : (dataSources || [])} />
      
      <LiveMetricsBar liveMetrics={effectiveLiveMetrics} />
      
      <TasksRecommendationsRow
        tasks={tasks}
        recommendations={recommendations}
        onToggleTask={onToggleTask}
        onExpandTask={onExpandTask}
        setActiveTab={setActiveTab}
      />
      
      <UpsellTeasers
        enabledModules={enabledModules}
        industry={industry}
        onExploreModule={(moduleId) => navigate(`/pricing?highlight=${moduleId}`)}
      />
      
      <QuickLinksSection industry={industry} />
      
      <CreditsWidget 
        compact={true} 
        onTopUp={() => navigate('/app/account')}
        data-testid="dashboard-credits-widget"
      />
    </div>
  );
};

export default OverviewTab;
